'use client'

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import Script from 'next/script'

declare global {
  interface Window {
    // Minimal typing for the Turnstile global injected by the script
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string
      remove: (id: string) => void
      getResponse?: (id: string) => string | null
      reset?: (id?: string) => void
    }
  }
}

export type TurnstileWidgetProps = {
  siteKey?: string
  action?: string
  appearance?: 'always' | 'execute' | 'interaction-only'
  theme?: 'auto' | 'light' | 'dark'
  size?: 'normal' | 'flexible' | 'compact'
  className?: string
  onToken: (token: string) => void
  onError?: (err?: unknown) => void
  onExpire?: () => void
  /**
   * When this prop changes, the widget will reset and issue a fresh token.
   * Pass a number like Date.now() or a counter from parent.
   */
  resetSignal?: unknown
  /**
   * Enable lazy loading - script won't load until shouldLoad is true
   */
  lazyLoad?: boolean
  /**
   * When true (and lazyLoad is enabled), triggers script load and widget render
   */
  shouldLoad?: boolean
}

/**
 * Lightweight wrapper for Cloudflare Turnstile using explicit rendering.
 * Renders a widget and invokes onToken when a token is issued/refreshed.
 */
export default function TurnstileWidget({
  siteKey,
  action,
  appearance = 'always',
  theme = 'auto',
  size = 'normal',
  className,
  onToken,
  onError,
  onExpire,
  resetSignal,
  lazyLoad = false,
  shouldLoad = false,
}: TurnstileWidgetProps) {
  const sitekey = useMemo(
    () => siteKey || process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '',
    [siteKey],
  )

  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const scriptLoadedRef = useRef(false)
  const widgetRenderedRef = useRef(false)
  const [scriptReady, setScriptReady] = useState(false)
  const [isLoadingScript, setIsLoadingScript] = useState(false)

  // Dynamically load the Turnstile script
  const loadTurnstileScript = useCallback(() => {
    // Check if script already exists or is loading
    if (scriptLoadedRef.current || window.turnstile || isLoadingScript) {
      if (window.turnstile) {
        setScriptReady(true)
      }
      return
    }

    setIsLoadingScript(true)

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true

    script.onload = () => {
      scriptLoadedRef.current = true
      setScriptReady(true)
      setIsLoadingScript(false)
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Turnstile] Script loaded successfully')
      }
    }

    script.onerror = (err) => {
      setIsLoadingScript(false)
      scriptLoadedRef.current = false
      console.error('[Turnstile] Failed to load script', err)
      onError?.(new Error('Failed to load Turnstile script'))
    }

    document.head.appendChild(script)
  }, [onError, isLoadingScript])

  // Load script when shouldLoad becomes true (for lazy loading)
  useEffect(() => {
    if (lazyLoad && shouldLoad && !scriptReady && !isLoadingScript) {
      loadTurnstileScript()
    }
  }, [lazyLoad, shouldLoad, scriptReady, isLoadingScript, loadTurnstileScript])

  // Render the widget when script is ready (for lazy loading)
  useEffect(() => {
    if (!lazyLoad || !scriptReady || widgetRenderedRef.current) return

    try {
      if (typeof window !== 'undefined' && window.turnstile && containerRef.current) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey,
          theme,
          size,
          appearance,
          action,
          callback: (token: string) => {
            try {
              if (process.env.NODE_ENV !== 'production') {
                const preview = typeof token === 'string' ? token.slice(0, 12) + '…' : token
                console.log('[Turnstile] token issued:', preview)
              }
              onToken(token)
            } catch (err) {
              onError?.(err)
            }
          },
          'error-callback': () => onError?.(),
          'expired-callback': () => {
            try {
              onExpire?.()
            } finally {
              onError?.()
            }
          },
        })
        widgetRenderedRef.current = true
      }
    } catch (err) {
      onError?.(err)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptReady, lazyLoad, sitekey, theme, size, appearance, action])

  // Render the widget when script is ready (for non-lazy loading - original behavior)
  const handleScriptReady = useCallback(() => {
    if (lazyLoad || widgetRenderedRef.current) return // Skip if lazy loading is enabled or already rendered

    try {
      if (typeof window !== 'undefined' && window.turnstile && containerRef.current) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey,
          theme,
          size,
          appearance,
          action,
          callback: (token: string) => {
            try {
              if (process.env.NODE_ENV !== 'production') {
                const preview = typeof token === 'string' ? token.slice(0, 12) + '…' : token
                console.log('[Turnstile] token issued:', preview)
              }
              onToken(token)
            } catch (err) {
              onError?.(err)
            }
          },
          'error-callback': () => onError?.(),
          'expired-callback': () => {
            try {
              onExpire?.()
            } finally {
              onError?.()
            }
          },
        })
        widgetRenderedRef.current = true
      }
    } catch (err) {
      onError?.(err)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyLoad, sitekey, theme, size, appearance, action])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        if (widgetIdRef.current && window?.turnstile) {
          window.turnstile.remove(widgetIdRef.current)
          widgetRenderedRef.current = false
        }
      } catch {
        /* noop */
      }
    }
  }, [])

  // Allow parent to force a reset to get a new token
  useEffect(() => {
    try {
      const id = widgetIdRef.current
      const api = typeof window !== 'undefined' ? window.turnstile : undefined
      if (resetSignal && id && api && typeof api.reset === 'function') {
        api.reset(id)
      }
    } catch {
      // noop
    }
  }, [resetSignal])

  if (!sitekey) return null

  // Lazy loading: show placeholder before script loads
  if (lazyLoad && !scriptReady) {
    return (
      <div className={className}>
        {!shouldLoad && (
          <button
            type="button"
            onClick={() => {
              // This shouldn't be needed as shouldLoad is controlled by parent
              // But kept as safety fallback
            }}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-md hover:bg-accent text-sm text-muted-foreground cursor-default"
            disabled
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-muted-foreground"
            >
              <rect
                x="3"
                y="3"
                width="14"
                height="14"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M7 10L9 12L13 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Click a form field to verify you&apos;re human
          </button>
        )}
        {shouldLoad && isLoadingScript && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Loading verification...
          </div>
        )}
        <div ref={containerRef} />
      </div>
    )
  }

  // Non-lazy loading: original behavior with Next.js Script component
  return (
    <>
      {!lazyLoad && (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
          strategy="afterInteractive"
          onReady={handleScriptReady}
        />
      )}
      <div ref={containerRef} className={className} />
    </>
  )
}
